<div class="w-full">
  <!-- EDIT MODE -->
  <div *ngIf="editable; else previewMode" class="space-y-6">
    <!-- Header -->
    <div
      class="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between"
    >
      <div>
        <h3 class="text-xl font-bold text-[#1E2A78]">
          {{ 'IRS_BUILDER.TITLE' | translate }}
        </h3>

        <p class="mt-1 text-sm text-slate-500">
          {{ 'IRS_BUILDER.SUBTITLE' | translate }}
        </p>
      </div>

      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          [disabled]="saving || loading"
          (click)="addHeading()"
          class="section-add-btn"
        >
          + {{ 'IRS_BUILDER.BLOCK_TYPE.HEADING' | translate }}
        </button>

        <button
          type="button"
          [disabled]="saving || loading"
          (click)="addParagraph()"
          class="section-add-btn"
        >
          + {{ 'IRS_BUILDER.BLOCK_TYPE.PARAGRAPH' | translate }}
        </button>

        <button
          type="button"
          [disabled]="saving || loading"
          (click)="addTable()"
          class="section-add-btn"
        >
          + {{ 'IRS_BUILDER.BLOCK_TYPE.TABLE' | translate }}
        </button>
      </div>
    </div>

    <!-- Messages -->
    <div
      *ngIf="errorMessage"
      role="alert"
      class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700"
    >
      {{ errorMessage }}
    </div>

    <div
      *ngIf="successMessage"
      class="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-700"
    >
      {{ successMessage }}
    </div>

    <div
      *ngIf="loading"
      class="flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-8 text-sm text-slate-500"
    >
      <span
        class="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-[#1E2A78]"
      ></span>
      {{ 'IRS_BUILDER.LOADING' | translate }}
    </div>

    <div
      *ngIf="saving"
      class="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm font-medium text-blue-700"
    >
      <span
        class="h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700"
      ></span>
      {{ 'IRS_BUILDER.SAVING' | translate }}
    </div>

    <!-- Empty State -->
    <div
      *ngIf="!loading && blocks.length === 0"
      class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center"
    >
      <h4 class="font-semibold text-slate-700">
        {{ 'IRS_BUILDER.NO_CONTENT' | translate }}
      </h4>

      <p class="mt-2 text-sm text-slate-500">
        {{ 'IRS_BUILDER.NO_CONTENT_HINT' | translate }}
      </p>
    </div>

    <!-- Blocks -->
    <div *ngIf="!loading" class="space-y-5" cdkDropList (cdkDropListDropped)="onBlockDrop($event)">
      <article
        *ngFor="
          let block of blocks;
          let blockIndex = index;
          trackBy: trackByBlock
        "
        cdkDrag
        cdkDragLockAxis="y"
        class="section-drag-item overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      >
        <!-- Accordion Header -->
        <div
          class="flex cursor-pointer items-center justify-between gap-4 border-b border-slate-100 bg-slate-50 px-5 py-4 transition hover:bg-slate-100"
          (click)="toggleBlock(block)"
        >
          <div class="flex min-w-0 items-center gap-3">
            <app-section-drag-handle cdkDragHandle (click)="$event.stopPropagation()"></app-section-drag-handle>

            <div class="min-w-0">
              <p
                class="text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-500"
              >
                {{ ('IRS_BUILDER.BLOCK_TYPE.' + (block.type | uppercase)) | translate }} {{ blockIndex + 1 }}
              </p>
  
              <h4 class="mt-1 truncate font-semibold text-slate-800">
                {{
                  block.title ||
                    ((block.type === 'heading'
                      ? 'IRS_BUILDER.UNTITLED_HEADING'
                      : block.type === 'paragraph'
                        ? 'IRS_BUILDER.UNTITLED_PARAGRAPH'
                        : 'IRS_BUILDER.UNTITLED_TABLE') | translate)
                }}
              </h4>
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-2">
            <button
              type="button"
              (click)="requestRemoveBlock(block.id); $event.stopPropagation()"
              class="section-delete-btn"
            >
              {{ 'COMMON.DELETE' | translate }}
            </button>

            <span
              class="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-slate-500"
            >
              {{ block.expanded ? '▲' : '▼' }}
            </span>
          </div>
        </div>

        <!-- Block Body -->
        <div *ngIf="block.expanded" class="p-5 sm:p-6">
          <!-- Heading Block -->
          <div *ngIf="block.type === 'heading'">
            <label class="mb-2 block text-sm font-semibold text-slate-700">
              {{ 'IRS_BUILDER.BLOCK_TYPE.HEADING' | translate }}
            </label>

            <input
              type="text"
              [(ngModel)]="block.title"
              [name]="'headingTitle_' + block.id"
              [placeholder]="'IRS_BUILDER.HEADING_PLACEHOLDER' | translate"
              class="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <!-- Paragraph Block -->
          <div *ngIf="block.type === 'paragraph'" class="space-y-5">
            <div>
              <label class="mb-2 block text-sm font-semibold text-slate-700">
                {{ 'IRS_BUILDER.OPTIONAL_PARAGRAPH_TITLE' | translate }}
              </label>

              <input
                type="text"
                [(ngModel)]="block.title"
                [name]="'paragraphTitle_' + block.id"
                [placeholder]="'IRS_BUILDER.OPTIONAL_TITLE' | translate"
                class="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            <div>
              <label class="mb-2 block text-sm font-semibold text-slate-700">
                {{ 'IRS_BUILDER.BLOCK_TYPE.PARAGRAPH' | translate }}
              </label>

              <app-rich-text-editor
                [(ngModel)]="block.content"
                [name]="'paragraphContent_' + block.id"
                minHeight="160px"
                [placeholder]="'IRS_BUILDER.PARAGRAPH_PLACEHOLDER' | translate"
              ></app-rich-text-editor>
            </div>
          </div>

          <!-- Table Block -->
          <section
            *ngIf="block.type === 'table' && block.table as table"
            class="space-y-5"
          >
            <div class="grid gap-5 lg:grid-cols-2">
              <div>
                <label class="mb-2 block text-sm font-semibold text-slate-700">
                  {{ 'IRS_BUILDER.TABLE_TITLE' | translate }}
                </label>

                <input
                  type="text"
                  [(ngModel)]="block.title"
                  [name]="'tableTitle_' + block.id"
                  [placeholder]="'IRS_BUILDER.OPTIONAL_TABLE_TITLE' | translate"
                  class="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              <div>
                <label class="mb-2 block text-sm font-semibold text-slate-700">
                  {{ 'IRS_BUILDER.TABLE_NOTE' | translate }}
                </label>

                <input
                  type="text"
                  [(ngModel)]="block.content"
                  [name]="'tableNote_' + block.id"
                  [placeholder]="'IRS_BUILDER.OPTIONAL_TABLE_NOTE' | translate"
                  class="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            </div>

            <!-- Table Toolbar -->
            <div
              class="flex flex-col gap-3 rounded-xl border border-indigo-100 bg-indigo-50 p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p class="text-sm font-semibold text-[#1E2A78]">
                  {{ 'IRS_BUILDER.TABLE_STATS' | translate: { headers: table.headers.length, columns: table.leafColumns.length, rows: table.rows.length } }}
                </p>

                <p class="mt-1 text-xs text-slate-500">
                  {{ 'IRS_BUILDER.HEADER_SPAN_HINT' | translate }}
                </p>
              </div>

              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  [disabled]="table.headers.length >= maxHeaders"
                  (click)="addHeader(block)"
                  class="section-add-btn"
                >
                  + {{ 'IRS_BUILDER.MAIN_HEADER' | translate }}
                </button>

                <button
                  type="button"
                  [disabled]="
                    table.leafColumns.length === 0 ||
                    table.rows.length >= maxRows
                  "
                  (click)="addRow(block)"
                  class="section-add-btn"
                >
                  + {{ 'IRS_BUILDER.ROW' | translate }}
                </button>
              </div>
            </div>

            <!-- Stable Table Builder -->
            <div
              *ngIf="table.headers.length > 0; else noHeaders"
              class="overflow-x-auto rounded-xl border border-slate-200"
            >
              <table class="w-full min-w-[900px] border-collapse text-sm">
                <thead>
                  <!-- Main Header Row -->
                  <tr>
                    <ng-container
                      *ngFor="
                        let header of table.headers;
                        trackBy: trackByHeader
                      "
                    >
                      <th
                        [attr.colspan]="header.subHeaders.length || 1"
                        [attr.rowspan]="
                          table.hasSubHeaders && header.subHeaders.length === 0
                            ? 2
                            : 1
                        "
                        class="min-w-[210px] border border-indigo-400 bg-[#1E2A78] p-3 align-top"
                      >
                        <div class="flex items-center gap-2">
                          <input
                            type="text"
                            [(ngModel)]="header.title"
                            [name]="'mainHeader_' + block.id + '_' + header.id"
                            [placeholder]="'IRS_BUILDER.MAIN_HEADER' | translate"
                            class="h-10 min-w-[110px] flex-1 rounded-lg border-0 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-indigo-200"
                          />

                          <button
                            type="button"
                            [disabled]="header.subHeaders.length >= maxSubHeaders"
                            (click)="addSubHeader(block, header.id)"
                            class="section-add-btn section-add-btn-sm"
                            [title]="'IRS_BUILDER.ADD_SUBHEADER' | translate"
                          >
                            + {{ 'IRS_BUILDER.SUB' | translate }}
                          </button>

                          <button
                            type="button"
                            (click)="removeHeader(block, header.id)"
                            class="section-delete-btn section-delete-btn-sm"
                            [title]="'IRS_BUILDER.DELETE_MAIN_HEADER' | translate"
                          >
                            {{ 'COMMON.DELETE' | translate }}
                          </button>
                        </div>
                      </th>
                    </ng-container>

                    <th
                      [attr.rowspan]="table.hasSubHeaders ? 2 : 1"
                      class="w-24 border border-indigo-400 bg-[#16205e] px-3 py-3 text-center font-semibold text-white"
                    >
                      {{ 'COMMON.ACTION' | translate }}
                    </th>
                  </tr>

                  <!-- Sub Header Row -->
                  <tr *ngIf="table.hasSubHeaders">
                    <ng-container
                      *ngFor="
                        let header of table.headers;
                        trackBy: trackByHeader
                      "
                    >
                      <th
                        *ngFor="
                          let subHeader of header.subHeaders;
                          trackBy: trackBySubHeader
                        "
                        class="min-w-[170px] border border-indigo-400 bg-[#26358f] p-3"
                      >
                        <div class="flex items-center gap-2">
                          <input
                            type="text"
                            [(ngModel)]="subHeader.title"
                            [name]="
                              'subHeader_' +
                              block.id +
                              '_' +
                              header.id +
                              '_' +
                              subHeader.id
                            "
                            [placeholder]="'IRS_BUILDER.SUB_HEADER' | translate"
                            class="h-10 min-w-[100px] flex-1 rounded-lg border-0 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:ring-2 focus:ring-indigo-200"
                          />

                          <button
                            type="button"
                            (click)="
                              removeSubHeader(
                                block,
                                header.id,
                                subHeader.id
                              )
                            "
                            class="section-delete-btn section-delete-btn-sm"
                            [title]="'IRS_BUILDER.DELETE_SUBHEADER' | translate"
                          >
                            {{ 'COMMON.DELETE' | translate }}
                          </button>
                        </div>
                      </th>
                    </ng-container>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    *ngFor="
                      let row of table.rows;
                      trackBy: trackByRow
                    "
                    class="transition hover:bg-indigo-50/40"
                  >
                    <td
                      *ngFor="
                        let leaf of table.leafColumns;
                        trackBy: trackByLeaf
                      "
                      class="border border-slate-200 p-3"
                    >
                      <input
                        type="text"
                        [(ngModel)]="row.cells[leaf.id]"
                        [name]="
                          'cell_' + block.id + '_' + row.id + '_' + leaf.id
                        "
                        [placeholder]="leaf.title || ('IRS_BUILDER.ENTER_VALUE' | translate)"
                        class="h-10 w-full min-w-[120px] rounded-lg border border-slate-300 px-3 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    </td>

                    <td class="border border-slate-200 p-3 text-center">
                      <button
                        type="button"
                        (click)="removeRow(block, row.id)"
                        class="section-delete-btn"
                      >
                        {{ 'COMMON.DELETE' | translate }}
                      </button>
                    </td>
                  </tr>

                  <tr *ngIf="table.rows.length === 0">
                    <td
                      [attr.colspan]="table.leafColumns.length + 1"
                      class="p-8 text-center text-sm text-slate-500"
                    >
                      {{ 'IRS_BUILDER.HEADERS_READY_PREFIX' | translate }}
                      <strong>+ {{ 'IRS_BUILDER.ROW' | translate }}</strong>
                      {{ 'IRS_BUILDER.HEADERS_READY_SUFFIX' | translate }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <ng-template #noHeaders>
              <div
                class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center"
              >
                <p class="font-semibold text-slate-700">
                  {{ 'IRS_BUILDER.NO_HEADERS_ADDED' | translate }}
                </p>

                <p class="mt-1 text-sm text-slate-500">
                  {{ 'IRS_BUILDER.NO_HEADERS_HINT' | translate }}
                </p>
              </div>
            </ng-template>
          </section>
        </div>
      </article>
    </div>

    <app-popup
      [(show)]="showDeletePopup"
      title="IRS_BUILDER.DELETE_BLOCK"
      type="confirm"
      [message]="deleteMessage"
      confirmText="COMMON.DELETE"
      cancelText="COMMON.CANCEL"
      (confirmed)="confirmRemoveBlock()"
    ></app-popup>
  </div>

  <!-- PREVIEW MODE -->
  <ng-template #previewMode>
    <div
      *ngIf="loading"
      class="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500"
    >
      {{ 'IRS_BUILDER.LOADING' | translate }}
    </div>

    <div
      *ngIf="!loading && blocks.length === 0"
      class="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500"
    >
      {{ 'IRS_BUILDER.NO_DATA' | translate }}
    </div>

    <div *ngIf="!loading" class="space-y-6">
      <section
        *ngFor="let block of blocks; trackBy: trackByBlock"
      >
        <!-- Heading Preview -->
        <h3
          *ngIf="block.type === 'heading'"
          class="text-lg font-semibold text-[#1E2A78]"
        >
          {{ block.title }}
        </h3>

        <!-- Paragraph Preview -->
        <div *ngIf="block.type === 'paragraph'">
          <h4
            *ngIf="block.title"
            class="mb-2 font-semibold text-[#1E2A78]"
          >
            {{ block.title }}
          </h4>

          <div
            class="rich-text-content text-justify text-slate-700"
            [innerHTML]="block.content | richText"
          ></div>
        </div>

        <!-- Table Preview -->
        <div *ngIf="block.type === 'table' && block.table as table">
          <h4
            *ngIf="block.title"
            class="mb-3 font-semibold text-[#1E2A78]"
          >
            {{ block.title }}
          </h4>

          <div
            *ngIf="table.headers.length > 0; else emptyPreviewTable"
            class="overflow-x-auto rounded-xl border border-slate-200"
          >
            <table class="w-full min-w-[700px] border-collapse text-sm">
              <thead>
                <tr>
                  <th
                    *ngFor="
                      let header of table.headers;
                      trackBy: trackByHeader
                    "
                    [attr.colspan]="header.subHeaders.length || 1"
                    [attr.rowspan]="
                      table.hasSubHeaders && header.subHeaders.length === 0
                        ? 2
                        : 1
                    "
                    class="border border-indigo-400 bg-[#1E2A78] px-4 py-3 text-center font-semibold text-white"
                  >
                    {{ header.title }}
                  </th>
                </tr>

                <tr *ngIf="table.hasSubHeaders">
                  <ng-container
                    *ngFor="
                      let header of table.headers;
                      trackBy: trackByHeader
                    "
                  >
                    <th
                      *ngFor="
                        let subHeader of header.subHeaders;
                        trackBy: trackBySubHeader
                      "
                      class="border border-indigo-400 bg-[#26358f] px-4 py-3 text-center font-semibold text-white"
                    >
                      {{ subHeader.title }}
                    </th>
                  </ng-container>
                </tr>
              </thead>

              <tbody>
                <tr *ngFor="let row of table.rows; trackBy: trackByRow">
                  <td
                    *ngFor="
                      let leaf of table.leafColumns;
                      trackBy: trackByLeaf
                    "
                    class="border border-slate-300 px-4 py-3 text-slate-700"
                    [class.text-center]="!(row.cells[leaf.id] | tableCell: leaf.title).numeric"
                    [class.text-end]="(row.cells[leaf.id] | tableCell: leaf.title).numeric"
                    [class.tabular-nums]="(row.cells[leaf.id] | tableCell: leaf.title).numeric"
                  >
                    {{ (row.cells[leaf.id] | tableCell: leaf.title).text || '-' }}
                  </td>
                </tr>

                <tr *ngIf="table.rows.length === 0">
                  <td
                    [attr.colspan]="table.leafColumns.length"
                    class="border border-slate-300 px-4 py-6 text-center text-slate-500"
                  >
                    {{ 'IRS_BUILDER.NO_TABLE_ROWS' | translate }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <ng-template #emptyPreviewTable>
            <div
              class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500"
            >
              {{ 'IRS_BUILDER.NO_TABLE_HEADERS' | translate }}
            </div>
          </ng-template>
          <p
            *ngIf="block.content"
            class="mt-3 whitespace-pre-line text-justify leading-7 text-slate-700"
          >
            {{ block.content }}
          </p>
        </div>
      </section>
    </div>
  </ng-template>
</div>